import * as angular from 'angular';

import { ComponentDefinition, ContextualField, IExtraButton, IExtraIcon, KeyValuePair, PropertyType } from '../../../models';
import { convertHeaderStyle } from '../../../states/scaledPrintPreview/convertHeaderStyle';
import { PageOption } from '../../../services/helpers/print/PageOption';
import { IPrintConfiguration } from '../../../services';
import { headerStyles } from '../../../common/constants';
import { PrintOptions } from '../../../services/helpers/print/PrintOptions';
import * as Fields from '../../../common/helpers/contextual';
import { ModernPrintPreviewContextualFormController } from './ModernPrintPreviewContextualFormController';


class ModernPrintPreviewContextualController implements angular.IComponentController {
    extraButtons: IExtraButton[];
    extraIcon: IExtraIcon;
    formController: ModernPrintPreviewContextualFormController;
    isTrackingDisabled: boolean;
    properties: ContextualField[];
    selectableOptions: KeyValuePair<PrintOptions, string>[];

    readonly exportToPdf: () => void;
    readonly isPrinting: boolean;
    readonly onPrintSystemChange: () => void;
    readonly options: PageOption[];
    readonly print: () => void;
    readonly printConfiguration: IPrintConfiguration;
    readonly save: () => angular.IPromise<void>;

    private _pristine: IPrintConfiguration;

    static $inject = [];

    $onInit() {
        this.initButtonsAndIcon(this.isPrinting);
        this.initProperties();
    }

    $onChanges(changes: angular.IOnChangesObject) {
        if (changes['isPrinting']) {
            this.initButtonsAndIcon(this.isPrinting);
        }
        if (changes['options']?.currentValue) {
            this.selectableOptions = this.options.map(option => {
                return { key: option.configuration, value: option.name };
            });
        }
        if (changes['printConfiguration']?.currentValue) {
            this.initPrintConfiguration();
        }

    }

    onSave(): angular.IPromise<void> {
        return this.save()
            .then(() => this.savePristine());
    }

    onCancel(): void {
        if (this._pristine) {
            const pristine = angular.copy(this._pristine);
            pristine.printOptions = this._pristine.printOptions;
            Object.assign(this.printConfiguration, pristine);
        }
    }

    private initPrintConfiguration(): void {
        if (this.printConfiguration) {
            convertHeaderStyle(this.printConfiguration);
            this.savePristine();
            this.initProperties();
        }
    }

    private savePristine(): void {
        const pristine = angular.copy(this.printConfiguration);
        // keys on select are sub-object, we must not work with a copy but with original
        pristine.printOptions = this.printConfiguration.printOptions;
        this._pristine = pristine;
    }

    private initButtonsAndIcon(isPrinting: boolean) {
        this.extraIcon = {
            title: 'Match settings on your printer. Do not forget to print background in images under print preferences.',
            icon: 'fa-lightbulb-o inline-help'
        };
        this.extraButtons = [
            {
                title: 'Print',
                icon: 'fa-print',
                click: () => this.print()
            },
            {
                title: 'Export to pdf',
                icon: isPrinting ? 'fa-circle-o-notch fa-spin' : 'fa-file-pdf-o',
                isDisabled: isPrinting,
                click: () => this.exportToPdf()
            }
        ];
    }

    private initProperties() {
        this.properties = [
            {
                modelName: 'printOptions',
                propertyType: PropertyType.Select,
                propertyName: 'printOptionsProperty',
                label: 'Print format',
                icon: ['fa-arrows-v', 'fa-arrows-h'],
                extraData: {
                    options: this.selectableOptions
                }
            },
            Object.assign({}, Fields.BackgroundColor),
            {
                modelName: 'headerStyle',
                propertyType: PropertyType.Select,
                propertyName: 'headerStyleProperty',
                label: 'Header',
                icon: 'fa-header',
                extraData: {
                    options: headerStyles
                }
            },
            {
                modelName: 'isHidingBorder',
                propertyType: PropertyType.Boolean,
                propertyName: 'isHidingBorderProperty',
                label: 'Hide border',
                icon: 'fa-eye-slash',
                iconText: '[]'
            },
            {
                modelName: 'isAutoStore',
                propertyType: PropertyType.Boolean,
                propertyName: 'isAutoStoreProperty',
                label: 'Save',
                icon: 'fa-archive'
            },
            {
                modelName: 'fileNameTemplate',
                propertyType: PropertyType.Text,
                propertyName: 'fileNameTemplateProperty',
                label: 'File name',
                icon: 'fa-file-text-o',
                required: false
            },
            {
                modelName: 'beginDateFormat',
                propertyType: PropertyType.Text,
                propertyName: 'beginDateFormatProperty',
                label: 'Begin date format',
                icon: 'fa-calendar-o',
                iconText: '[',
                isIconTextLast: true,
                required: false
            },
            {
                modelName: 'endDateFormat',
                propertyType: PropertyType.Text,
                propertyName: 'endDateFormatProperty',
                label: 'End date format',
                icon: 'fa-calendar-o',
                iconText: ']',
                isIconTextLast: true,
                required: false
            },
            {
                modelName: 'isUseNewPrintSystem',
                propertyType: PropertyType.Boolean,
                propertyName: 'isUseNewPrintSystemProperty',
                label: 'Use new print system',
                icon: ['fa-object-group', 'fa-print'],
                onChange: (viewValue) => {
                    if (this.save) {
                        this.printConfiguration.isUseNewPrintSystem = !!viewValue;
                        this.save()
                            .then(() => {
                                this.formController?.$setPristine();
                                if (this.onPrintSystemChange) {
                                    this.onPrintSystemChange();
                                }
                            });
                    }
                }
            }
        ];
    }
}

export const wtModernPrintPreviewContextual: ComponentDefinition = {
    componentId: 'wtModernPrintPreviewContextual',
    controller: ModernPrintPreviewContextualController,
    template: require('./modernPrintPreviewContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        exportToPdf: '&',
        formController: '=',
        isPrinting: '<',
        isTrackingDisabled: '<?',
        onPrintSystemChange: '&?',
        options: '<',
        print: '&',
        printConfiguration: '<',
        save: '&'
    }
};